import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
  type Router,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import type { Role } from '@/lib/types';

declare module 'vue-router' {
  interface RouteMeta {
    /** Must be signed in. */
    auth?: boolean;
    /** Must hold one of these roles. Implies `auth`. */
    roles?: Role[];
    title?: string;
    description?: string;
    /** Drill-down page: slides in on push, out on back (see App.vue). */
    slide?: boolean;
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/public/HomePage.vue'),
    meta: {
      title: 'ServiceAndGo — find trusted local services',
      description:
        'Book verified, reviewed local pros — plumbers, electricians, cleaners and more — in a few taps.',
    },
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('@/pages/public/SearchPage.vue'),
    meta: { title: 'Search services' },
  },
  {
    // Discovery, not text search: categories and interests. The header search icon
    // still opens the global search overlay (#search). Detail views are their own
    // routes so the phone back button pops a category/interest, not the whole page.
    path: '/browse',
    name: 'browse',
    component: () => import('@/pages/public/BrowsePage.vue'),
    meta: { title: 'Browse services' },
  },
  {
    path: '/browse/category/:slug',
    name: 'browse-category',
    component: () => import('@/pages/public/BrowseCategoryPage.vue'),
    props: true,
    meta: { title: 'Browse services' },
  },
  {
    path: '/browse/interest/:slug',
    name: 'browse-interest',
    component: () => import('@/pages/public/BrowseInterestPage.vue'),
    props: true,
    meta: { title: 'Browse services' },
  },
  {
    path: '/services/:id',
    name: 'listing',
    component: () => import('@/pages/public/ListingPage.vue'),
    props: true,
  },
  {
    path: '/services/:id/reviews',
    name: 'listing-reviews',
    component: () => import('@/pages/public/ListingReviewsPage.vue'),
    props: true,
    meta: { title: 'Reviews' },
  },
  {
    // Reached by tapping a date on the listing's calendar; `:date` is a
    // `YYYY-MM-DD` key in the provider's own calendar, not an instant.
    path: '/services/:id/book/:date',
    name: 'listing-book',
    component: () => import('@/pages/public/BookingTimePage.vue'),
    props: true,
    meta: { title: 'Choose a time', slide: true },
  },
  {
    path: '/providers/:slug',
    name: 'provider',
    component: () => import('@/pages/public/ProviderPage.vue'),
    props: true,
  },
  {
    // Public share target for a provider's portfolio collection.
    path: '/providers/:slug/portfolio',
    name: 'provider-portfolio',
    component: () => import('@/pages/public/PortfolioPage.vue'),
    props: true,
    meta: { title: 'Portfolio' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/public/LoginPage.vue'),
    meta: { title: 'Sign in' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/public/ForgotPasswordPage.vue'),
    meta: { title: 'Reset your password' },
  },
  {
    // Public, and must stay so: the holder of the mail is signed out by definition.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/public/ResetPasswordPage.vue'),
    meta: { title: 'Choose a new password' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/pages/public/VerifyEmailPage.vue'),
    meta: { title: 'Confirm your email' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/public/RegisterPage.vue'),
    meta: { title: 'Create an account' },
  },
  {
    path: '/favorites',
    name: 'favorites',
    component: () => import('@/pages/customer/FavoritesPage.vue'),
    meta: { auth: true, title: 'Saved' },
  },
  {
    // Both sides of a booking live here; `GET /bookings` scopes itself by role.
    path: '/bookings',
    name: 'bookings',
    component: () => import('@/pages/customer/BookingsPage.vue'),
    meta: { auth: true, title: 'Your bookings' },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/customer/NotificationsPage.vue'),
    meta: { auth: true, title: 'Notifications' },
  },
  {
    path: '/messages',
    name: 'messages',
    component: () => import('@/pages/customer/MessagesPage.vue'),
    meta: { auth: true, title: 'Messages' },
  },
  {
    path: '/messages/:id',
    name: 'conversation',
    component: () => import('@/pages/customer/ConversationPage.vue'),
    props: true,
    meta: { auth: true, title: 'Messages' },
  },
  {
    // Public: signed-out visitors get sign-in prompts rather than a bounce.
    path: '/profile',
    name: 'profile',
    component: () => import('@/pages/customer/ProfilePage.vue'),
    meta: { title: 'Profile' },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/pages/customer/AccountPage.vue'),
    meta: { auth: true, title: 'Account details' },
  },
  {
    path: '/preferences',
    name: 'preferences',
    component: () => import('@/pages/customer/PreferencesPage.vue'),
    meta: { auth: true, title: 'Preferences' },
  },
  {
    path: '/preferences/notifications',
    name: 'preferences-notifications',
    component: () => import('@/pages/customer/NotificationPreferencesPage.vue'),
    meta: { auth: true, title: 'Notification preferences' },
  },
  {
    path: '/preferences/appearance',
    name: 'preferences-appearance',
    component: () => import('@/pages/customer/AppearancePage.vue'),
    meta: { auth: true, title: 'Appearance', slide: true },
  },
  {
    path: '/preferences/language',
    name: 'preferences-language',
    component: () => import('@/pages/customer/LanguagePage.vue'),
    meta: { auth: true, title: 'Language', slide: true },
  },
  {
    path: '/preferences/currency',
    name: 'preferences-currency',
    component: () => import('@/pages/customer/CurrencyPage.vue'),
    meta: { auth: true, title: 'Currency', slide: true },
  },
  {
    path: '/preferences/security',
    name: 'preferences-security',
    component: () => import('@/pages/customer/SecurityPage.vue'),
    meta: { auth: true, title: 'Security' },
  },
  {
    path: '/provider',
    name: 'provider-dashboard',
    component: () => import('@/pages/provider/DashboardPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Provider dashboard' },
  },
  {
    path: '/provider/listings',
    name: 'provider-listings',
    component: () => import('@/pages/provider/ListingsPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Your listings' },
  },
  {
    // Before `/provider/listings/:id`, or "new" would be read as a listing id.
    path: '/provider/listings/new',
    name: 'provider-listing-new',
    component: () => import('@/pages/provider/ListingEditorPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'New listing' },
  },
  {
    path: '/provider/listings/:id',
    name: 'provider-listing-edit',
    component: () => import('@/pages/provider/ListingEditorPage.vue'),
    props: true,
    meta: { roles: ['PROVIDER'], title: 'Edit listing' },
  },
  {
    path: '/provider/portfolios',
    name: 'provider-portfolios',
    component: () => import('@/pages/provider/PortfolioEditorPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Your portfolio' },
  },
  {
    // Before `/provider/portfolios/:id`, or "new" would be read as a portfolio id.
    path: '/provider/portfolios/new',
    name: 'provider-portfolio-new',
    component: () => import('@/pages/provider/PortfolioEditPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'New portfolio' },
  },
  {
    path: '/provider/portfolios/:id',
    name: 'provider-portfolio-edit',
    component: () => import('@/pages/provider/PortfolioEditPage.vue'),
    props: true,
    meta: { roles: ['PROVIDER'], title: 'Edit portfolio' },
  },
  {
    path: '/provider/profile',
    name: 'provider-profile',
    component: () => import('@/pages/provider/ProviderProfilePage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Business profile' },
  },
  {
    path: '/provider/verification',
    name: 'provider-verification',
    component: () => import('@/pages/provider/VerificationPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Verify your business' },
  },
  {
    path: '/provider/schedule',
    name: 'provider-schedule',
    component: () => import('@/pages/provider/SchedulePage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Your schedule' },
  },
  {
    // The payment gateway returns the provider here with `?status=success|cancelled`.
    path: '/provider/subscription',
    name: 'provider-subscription',
    component: () => import('@/pages/provider/SubscriptionPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Subscription' },
  },
  {
    path: '/provider/billing',
    name: 'provider-billing',
    component: () => import('@/pages/provider/BillingPage.vue'),
    meta: { roles: ['PROVIDER'], title: 'Billing history' },
  },
  {
    path: '/provider/billing/:id/receipt',
    name: 'provider-billing-receipt',
    component: () => import('@/pages/provider/ReceiptPage.vue'),
    props: true,
    meta: { roles: ['PROVIDER'], title: 'Receipt' },
  },
  {
    path: '/admin/verifications',
    name: 'admin-verifications',
    component: () => import('@/pages/admin/VerificationsPage.vue'),
    meta: { roles: ['ADMIN'], title: 'Verification queue' },
  },
  {
    path: '/admin/reports',
    name: 'admin-reports',
    component: () => import('@/pages/admin/ReportsPage.vue'),
    meta: { roles: ['ADMIN'], title: 'Reports' },
  },
  {
    path: '/admin/reports/:id',
    name: 'admin-report',
    component: () => import('@/pages/admin/ReportDetailPage.vue'),
    props: true,
    meta: { roles: ['ADMIN'], title: 'Report' },
  },
  {
    path: '/admin/announcements',
    name: 'admin-announcements',
    component: () => import('@/pages/admin/AnnouncementsPage.vue'),
    meta: { roles: ['ADMIN'], title: 'Announcements' },
  },
  {
    path: '/admin/audit',
    name: 'admin-audit',
    component: () => import('@/pages/admin/AuditLogPage.vue'),
    meta: { roles: ['ADMIN'], title: 'Audit trail' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/public/NotFoundPage.vue'),
    meta: { title: 'Page not found' },
  },
];

export type GuardResult = true | { name: string; query?: Record<string, string> };

/**
 * Pure so it can be tested without mounting a router.
 *
 * `roles` implies `auth`: a route restricted to providers is meaningless to
 * someone who is not signed in, and forgetting to write both is an easy way to
 * ship an unguarded page.
 */
export function resolveGuard(
  to: Pick<RouteLocationNormalized, 'meta' | 'fullPath' | 'name'>,
  session: { isAuthenticated: boolean; role: Role | null },
): GuardResult {
  const needsAuth = to.meta.auth === true || (to.meta.roles?.length ?? 0) > 0;

  if (needsAuth && !session.isAuthenticated) {
    // Carry where they were going, so signing in resumes the journey.
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (to.meta.roles && (!session.role || !to.meta.roles.includes(session.role))) {
    return { name: 'not-found' };
  }

  // A signed-in user has no business on the login page.
  if ((to.name === 'login' || to.name === 'register') && session.isAuthenticated) {
    return { name: 'home' };
  }

  return true;
}

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  });

  router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // On a hard reload the access token is gone (it lives in memory) but the
    // refresh cookie is not. Guarding before that resolves would bounce a
    // signed-in user to the login page on every refresh of a protected page.
    if (!auth.ready) await auth.restore();

    const result = resolveGuard(to, { isAuthenticated: auth.isAuthenticated, role: auth.role });
    return result === true ? true : result;
  });

  // SEO: the public pages are what search engines index, and a SPA changes the
  // title on navigation, not on load.
  router.afterEach((to) => {
    const title = to.meta.title ?? 'ServiceAndGo';
    document.title = title;
    if (to.meta.description) {
      document.querySelector('meta[name="description"]')?.setAttribute('content', to.meta.description);
    }

    // In a SPA the tapped link outlives the navigation, so it keeps focus — and
    // WKWebView paints :focus-visible for that, leaving a ring stuck on the tab
    // bar after every tap. A new page starts with focus on none of the old one.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  });

  return router;
}
